import { addYears } from 'date-fns';
import {
  fetchContractCoveragesByPlan,
  fetchRiskAmount,
  requestContractPrices,
} from '../../api/contractApi';
import { APPLICATION_CONTRACT_TYPES } from '../../constants';
import { createReducerWithName } from '../middlewares/createReducerWithName';
import { PAYMENT_FREQUENCIES, SINGLE } from '../../constants/paymentFrequency';
import { getArmenianStartDate } from '../../helpers/dates';

export const SET_RELEASE_YEAR = 'efes/roadCareCalculation/SET_RELEASE_YEAR';
export const SET_TRANSPORT_USAGE_TYPE = 'efes/roadCareCalculation/SET_TRANSPORT_USAGE_TYPE';

const GET_RISK_AMOUNTS = 'efes/roadCareCalculation/GET_RISK_AMOUNTS';
const GET_RISK_AMOUNTS_SUCCESS = 'efes/roadCareCalculation/GET_RISK_AMOUNTS_SUCCESS';
const GET_RISK_AMOUNTS_FAIL = 'efes/roadCareCalculation/GET_RISK_AMOUNTS_FAIL';

const GET_CONTRACT_COVERAGES = 'efes/roadCareCalculation/GET_CONTRACT_COVERAGES';
const GET_CONTRACT_COVERAGES_SUCCESS = 'efes/roadCareCalculation/GET_CONTRACT_COVERAGES_SUCCESS';
const GET_CONTRACT_COVERAGES_FAIL = 'efes/roadCareCalculation/GET_CONTRACT_COVERAGES_FAIL';

const CLEAR_CALCULATION = 'efes/roadCareCalculation/CLEAR_CALCULATION';

export const GET_PRICES = 'efes/roadCareCalculation/GET_PRICES';
export const GET_PRICES_SUCCESS = 'efes/roadCareCalculation/GET_PRICES_SUCCESS';
export const GET_PRICES_FAIL = 'efes/roadCareCalculation/GET_PRICES_FAIL';

export const SET_MOBILE_CONTRACT_DATA = 'efes/roadCareCalculation/SET_MOBILE_CONTRACT_DATA';
export const SET_MOBILE_CONTRACT_DATA_SUCCESS = 'efes/roadCareCalculation/SET_MOBILE_CONTRACT_DATA_SUCCESS';
export const SET_MOBILE_CONTRACT_DATA_FAIL = 'efes/roadCareCalculation/SET_MOBILE_CONTRACT_DATA_FAIL';

const initialState = {
  releaseYear: 0,
  transportUsageType: {},
  defaultCoverages: [],
  isFetchingDefaultCoverages: false,
  riskAmount: 0,
  riskAmounts: [],
  isFetchingRiskAmounts: false,
  fetchRiskAmountsError: null,
  isFetchingPrice: false,
  prices: {},
  paymentFrequency: PAYMENT_FREQUENCIES[SINGLE],
  paymentFrequencies: {},
  riskAdverse: 0,
  loadingMobileContractData: false,
  range: {
    start: getArmenianStartDate(2),
    end: addYears(getArmenianStartDate(1), 1),
  },
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_RELEASE_YEAR:
      return {
        ...state,
        releaseYear: action.data,
      };
    case SET_TRANSPORT_USAGE_TYPE:
      return {
        ...state,
        transportUsageType: action.data,
      };
    case GET_RISK_AMOUNTS:
      return {
        ...state,
        isFetchingRiskAmounts: true,
      };
    case GET_RISK_AMOUNTS_SUCCESS:
      return {
        ...state,
        isFetchingRiskAmounts: false,
        riskAmounts: action.result.amounts,
        riskAmount: action.result.defaultAmounts,
      };
    case GET_RISK_AMOUNTS_FAIL:
      return {
        ...state,
        isFetchingRiskAmounts: false,
        fetchRiskAmountsError: action.error,
      };

    case GET_CONTRACT_COVERAGES:
      return {
        ...state,
        isFetchingDefaultCoverages: true,
      };
    case GET_CONTRACT_COVERAGES_SUCCESS:
      return {
        ...state,
        defaultCoverages: action.result,
        isFetchingDefaultCoverages: false,
      };
    case GET_CONTRACT_COVERAGES_FAIL:
      return {
        ...state,
        defaultCoverages: [],
        isFetchingDefaultCoverages: false,
      };
    case GET_PRICES:
      return {
        ...state,
        isFetchingPrice: true,
      };
    case GET_PRICES_SUCCESS:
      return {
        ...state,
        isFetchingPrice: false,
        prices: action.result.price,
        paymentFrequencies: action.result.paymentFrequency,
        riskAdverse: action.result.riskAdverse,
      };
    case GET_PRICES_FAIL:
      return {
        ...state,
        isFetchingPrice: false,
        fetchPricesError: action.error,
      };
    case SET_MOBILE_CONTRACT_DATA:
      return {
        ...state,
        loadingMobileContractData: true,
      };
    case SET_MOBILE_CONTRACT_DATA_SUCCESS:
      return {
        ...state,
        ...(action.result && action.result),
        loadingMobileContractData: false,
      };
    case SET_MOBILE_CONTRACT_DATA_FAIL:
      return {
        ...state,
        loadingMobileContractData: false,
      };
    case CLEAR_CALCULATION:
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

export const setReleaseYear = (year, name) => ({
  type: SET_RELEASE_YEAR,
  data: year,
  name,
});

export const setTransportUsageType = (transportUsageType, name) => ({
  type: SET_TRANSPORT_USAGE_TYPE,
  data: transportUsageType,
  name,
});

export const clearCalculation = (name) => ({
  type: CLEAR_CALCULATION,
  name,
});

export const getRiskAmounts = (name, template) => (dispatch) => {
  dispatch({
    actions: [
      { type: GET_RISK_AMOUNTS, name },
      { type: GET_RISK_AMOUNTS_SUCCESS, name },
      {
        type: GET_RISK_AMOUNTS_FAIL,
        name,
        showError: true,
      },
    ],
    promise: fetchRiskAmount(template).then((response) => response.data),
  });
};

export const getContractCoverages = (name, contractType, planLabel) => (dispatch) => {
  dispatch({
    actions: [
      { type: GET_CONTRACT_COVERAGES, name },
      { type: GET_CONTRACT_COVERAGES_SUCCESS, name },
      {
        type: GET_CONTRACT_COVERAGES_FAIL,
        name,
        showError: true,
      },
    ],
    promise: fetchContractCoveragesByPlan(contractType, planLabel)
      .then((response) => response.data),
  });
};

export const getPrices = (data, name) => (dispatch) => {
  dispatch({
    actions: [
      { type: GET_PRICES, name },
      { type: GET_PRICES_SUCCESS, name },
      {
        type: GET_PRICES_FAIL,
        name,
        showError: true,
        customMsg: 'getPricesFail',
      },
    ],
    promise: requestContractPrices(data, APPLICATION_CONTRACT_TYPES.ROAD_CARE)
      .then((response) => response.data),
  });
};

export const roadCareCalculationReducerForContract = createReducerWithName(
  'contract',
  reducer,
  initialState,
);
